import type { Booking } from '@/components/BookingCalendar';

export type BookingMetadata = {
  createdAt: string;
  lastModified?: string;
  originalBookingId?: number;
  duplicatedAt?: string;
  duplicateVersion?: number;
  relationships?: {
    parentId?: number;
    childIds?: number[];
    linkedBookings?: number[];
    syncFields?: {
      status: boolean;
      amount: boolean;
      notes: boolean;
      duration: boolean;
    };
  };
  tags?: string[];
  customFields?: Record<string, any>;
};

export type DuplicateOptions = {
  maintainRelationship: boolean;
  copyTags: boolean;
  syncFields: {
    status: boolean;
    amount: boolean;
    notes: boolean;
    duration: boolean;
  };
};

export function generateBookingMetadata(
  booking: Booking, 
  options?: DuplicateOptions
): BookingMetadata {
  const now = new Date().toISOString();
  
  return {
    createdAt: booking.metadata?.createdAt || now,
    lastModified: now,
    originalBookingId: booking.metadata?.originalBookingId,
    duplicatedAt: booking.metadata?.duplicatedAt,
    duplicateVersion: booking.metadata?.duplicateVersion,
    relationships: {
      ...booking.metadata?.relationships,
      linkedBookings: options?.maintainRelationship 
        ? booking.metadata?.relationships?.linkedBookings || []
        : [],
      syncFields: options?.maintainRelationship
        ? options.syncFields
        : undefined
    },
    tags: options?.copyTags ? [...(booking.metadata?.tags || [])] : [],
    customFields: options?.copyTags 
      ? { ...(booking.metadata?.customFields || {}) }
      : {}
  };
}

export function isBookingDuplicate(booking: Booking): boolean {
  return !!booking.metadata?.originalBookingId;
}

export function getDuplicateVersion(booking: Booking): number {
  return booking.metadata?.duplicateVersion || 0;
}

export function getOriginalBooking(booking: Booking, bookings: Booking[]): Booking | null {
  if (!booking.metadata?.originalBookingId) return null;
  return bookings.find(b => b.id === booking.metadata?.originalBookingId) || null;
}

export function getAllDuplicates(booking: Booking, bookings: Booking[]): Booking[] {
  const originalId = booking.metadata?.originalBookingId || booking.id;
  return bookings.filter(b => 
    b.metadata?.originalBookingId === originalId || 
    b.id === originalId
  ).sort((a, b) => getDuplicateVersion(a) - getDuplicateVersion(b));
}

export function getLinkedBookings(booking: Booking, bookings: Booking[]): Booking[] {
  const linkedIds = booking.metadata?.relationships?.linkedBookings || [];
  return bookings.filter(b => linkedIds.includes(b.id));
}

export function shouldSyncField(
  booking: Booking,
  fieldName: keyof DuplicateOptions['syncFields']
): boolean {
  return !!booking.metadata?.relationships?.syncFields?.[fieldName];
}

export function updateLinkedBookings(
  booking: Booking,
  bookings: Booking[],
  updates: Partial<Booking>
): Booking[] {
  const linkedBookings = getLinkedBookings(booking, bookings);
  
  return linkedBookings.map(linked => {
    const syncFields = linked.metadata?.relationships?.syncFields;
    if (!syncFields) return linked;

    const updatedFields: Partial<Booking> = {};
    
    if (syncFields.status && 'status' in updates) {
      updatedFields.status = updates.status;
    }
    if (syncFields.amount && 'revenue' in updates) {
      updatedFields.revenue = {
        ...linked.revenue,
        amount: updates.revenue?.amount
      };
    }
    if (syncFields.notes && 'revenue' in updates) {
      updatedFields.revenue = {
        ...linked.revenue,
        notes: updates.revenue?.notes,
        hasNotes: updates.revenue?.hasNotes
      };
    }
    if (syncFields.duration && 'duration' in updates) {
      updatedFields.duration = updates.duration;
    }

    return {
      ...linked,
      ...updatedFields,
      metadata: {
        ...linked.metadata,
        lastModified: new Date().toISOString()
      }
    };
  });
}